import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, FolderOpen, Package, ShoppingBag, ClipboardList,
  Users, Star, Ticket, Image, Truck, Monitor, Home, FileText, Phone,
  HelpCircle, RotateCcw, ScrollText, Lock, Palette, Plug2, Megaphone,
  MapPin, Radio, TrendingUp, Link as LinkIcon, Music2, Tag, Globe, User,
  ChevronLeft, ChevronRight, ChevronDown, RefreshCw, ExternalLink, LogOut, Menu, X,
  Moon, Sun,
} from 'lucide-react';
import './AdminLayout.css';

interface MenuItem {
  path: string;
  icon: React.ReactNode;
  label: string;
}

interface MenuGroup {
  icon: React.ReactNode;
  label: string;
  children: MenuItem[];
}

type SidebarItem = MenuItem | MenuGroup;

function isMenuGroup(item: SidebarItem): item is MenuGroup {
  return 'children' in item;
}

const AdminLayout: React.FC = () => {
  const { i18n } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('admin-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });

  const isExpanded = sidebarOpen || mobileOpen;

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  React.useEffect(() => {
    localStorage.setItem('admin-dark-mode', JSON.stringify(darkMode));
    
    // Disable transitions temporarily for smooth theme switch
    document.documentElement.classList.add('theme-transitioning');
    
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Re-enable transitions after a brief delay
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning');
    }, 50);
  }, [darkMode]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const sidebarItems: SidebarItem[] = [
    { path: '/admin/dashboard',       icon: <LayoutDashboard size={18} />, label: 'Dashboard' },
    { path: '/admin/categories',      icon: <FolderOpen size={18} />,      label: 'Categories' },
    { path: '/admin/products',        icon: <Package size={18} />,         label: 'Products' },
    { path: '/admin/orders',          icon: <ShoppingBag size={18} />,     label: 'Orders' },
    { path: '/admin/fulfillment',     icon: <ClipboardList size={18} />,   label: 'Fulfillment' },
    { path: '/admin/customers',       icon: <Users size={18} />,           label: 'Customers' },
    { path: '/admin/reviews',         icon: <Star size={18} />,            label: 'Reviews' },
    { path: '/admin/coupons',         icon: <Ticket size={18} />,          label: 'Coupons' },
    { path: '/admin/banners',         icon: <Image size={18} />,           label: 'Banners' },
    { path: '/admin/delivery-options',icon: <Truck size={18} />,           label: 'Delivery Options' },
    {
      icon: <Monitor size={18} />, label: 'CMS',
      children: [
        { path: '/admin/cms/home',     icon: <Home size={16} />,       label: 'Home Page' },
        { path: '/admin/cms/about',    icon: <FileText size={16} />,   label: 'About Us' },
        { path: '/admin/cms/contact',  icon: <Phone size={16} />,      label: 'Contact' },
        { path: '/admin/cms/faq',      icon: <HelpCircle size={16} />, label: 'FAQ' },
        { path: '/admin/cms/shipping', icon: <Truck size={16} />,      label: 'Shipping Policy' },
        { path: '/admin/cms/returns',  icon: <RotateCcw size={16} />,  label: 'Returns' },
        { path: '/admin/cms/terms',    icon: <ScrollText size={16} />, label: 'Terms' },
        { path: '/admin/cms/privacy',  icon: <Lock size={16} />,       label: 'Privacy Policy' },
      ],
    },
    { path: '/admin/settings',    icon: <Palette size={18} />,  label: 'Website Settings' },
    { path: '/admin/integrations',icon: <Plug2 size={18} />,    label: 'Integrations' },
    {
      icon: <Megaphone size={18} />, label: 'Meta Ads',
      children: [
        { path: '/admin/meta-ads/profit',   icon: <TrendingUp size={16} />, label: 'Ads Profit & Analytics' },
        { path: '/admin/pixel',             icon: <MapPin size={16} />,     label: 'Facebook Pixel' },
        { path: '/admin/capi',              icon: <Radio size={16} />,      label: 'Facebook CAPI' },
        { path: '/admin/meta-ads/config',   icon: <Radio size={16} />,      label: 'Ads Config' },
        { path: '/admin/meta-ads/report',   icon: <TrendingUp size={16} />, label: 'Ads Report' },
        { path: '/admin/meta-ads/products', icon: <LinkIcon size={16} />,   label: 'Campaign Products' },
      ],
    },
    {
      icon: <Music2 size={18} />, label: 'TikTok',
      children: [
        { path: '/admin/tiktok-ads-profit', icon: <TrendingUp size={16} />, label: 'Ads Profit & Analytics' },
        { path: '/admin/tiktok-pixel',      icon: <MapPin size={16} />, label: 'TikTok Pixel' },
        { path: '/admin/tiktok-events-api', icon: <Radio size={16} />,  label: 'TikTok Events API' },
      ],
    },
    { path: '/admin/gtm',          icon: <Tag size={18} />,    label: 'Google Tag Manager' },
    { path: '/admin/translations', icon: <Globe size={18} />,  label: 'Translations' },
    { path: '/admin/profile',      icon: <User size={18} />,   label: 'My Profile' },
  ];

  const isGroupActive = (group: MenuGroup) =>
    group.children.some((child) => location.pathname.startsWith(child.path));

  const isGroupOpen = (label: string, group: MenuGroup) =>
    openMenus[label] !== undefined ? openMenus[label] : isGroupActive(group);

  const toggleGroup = (label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const getPageTitle = (): string => {
    for (const item of sidebarItems) {
      if (isMenuGroup(item)) {
        const match = item.children.find((c) => location.pathname.startsWith(c.path));
        if (match) return `${item.label} › ${match.label}`;
      } else if (location.pathname.startsWith(item.path)) {
        return item.label;
      }
    }
    return 'Admin';
  };

  return (
    <div className={`admin-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'} ${mobileOpen ? 'mobile-nav-open' : ''}`}>
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'} ${mobileOpen ? 'mobile-visible' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            E-Store Admin
          </Link>
          <button
            className="toggle-sidebar-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
          <button
            className="mobile-close-sidebar-btn"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul>
            {sidebarItems.map((item) =>
              isMenuGroup(item) ? (
                <li key={item.label} className="nav-group">
                  <button
                    className={`nav-item nav-group-header ${isGroupActive(item) ? 'active' : ''}`}
                    onClick={() => toggleGroup(item.label)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {isExpanded && <span className="nav-label">{item.label}</span>}
                    {isExpanded && (
                      <span className={`nav-group-chevron ${isGroupOpen(item.label, item) ? 'open' : ''}`}>
                        <ChevronDown size={15} />
                      </span>
                    )}
                  </button>
                  {isExpanded && isGroupOpen(item.label, item) && (
                    <ul className="nav-submenu">
                      {item.children.map((child) => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            className={`nav-item nav-subitem ${location.pathname.startsWith(child.path) ? 'active' : ''}`}
                            onClick={() => setMobileOpen(false)}
                          >
                            <span className="nav-icon">{child.icon}</span>
                            <span className="nav-label">{child.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ) : (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`nav-item ${location.pathname.startsWith(item.path) ? 'active' : ''}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {isExpanded && <span className="nav-label">{item.label}</span>}
                  </Link>
                </li>
              )
            )}
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <div className="header-left">
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation"
            >
              <Menu size={22} />
            </button>
            <h2 className="page-title">
              {getPageTitle()}
            </h2>
          </div>
          <div className="header-right">
             <button
               className="refresh-btn"
               onClick={() => window.dispatchEvent(new CustomEvent('admin-refresh'))}
               title="Refresh page data"
               style={{
                 padding: '0.4rem 0.75rem',
                 borderRadius: '6px',
                 border: darkMode ? '1px solid #4b5563' : '1px solid #e5e7eb',
                 background: darkMode ? '#374151' : 'white',
                 color: darkMode ? '#e2e8f0' : '#6b7280',
                 fontSize: '0.8rem',
                 cursor: 'pointer',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '4px',
                 transition: 'all 0.2s',
               }}
               onMouseEnter={(e) => {
                 e.currentTarget.style.borderColor = '#0284c7';
                 e.currentTarget.style.color = '#0284c7';
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.borderColor = darkMode ? '#4b5563' : '#e5e7eb';
                 e.currentTarget.style.color = darkMode ? '#e2e8f0' : '#6b7280';
               }}
             >
               <RefreshCw size={14} />
             </button>
             <button
               className="dark-mode-toggle"
               onClick={() => setDarkMode(!darkMode)}
               title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
               style={{
                 padding: '0.4rem 0.75rem',
                 borderRadius: '6px',
                 border: '1px solid #e5e7eb',
                 background: darkMode ? '#374151' : 'white',
                 color: darkMode ? '#fbbf24' : '#6b7280',
                 fontSize: '0.8rem',
                 cursor: 'pointer',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '4px',
                 transition: 'all 0.2s',
               }}
               onMouseEnter={(e) => {
                 e.currentTarget.style.borderColor = darkMode ? '#fbbf24' : '#0284c7';
                 e.currentTarget.style.color = darkMode ? '#fbbf24' : '#0284c7';
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.borderColor = darkMode ? '#4b5563' : '#e5e7eb';
                 e.currentTarget.style.color = darkMode ? '#fbbf24' : '#6b7280';
               }}
             >
               {darkMode ? <Sun size={14} /> : <Moon size={14} />}
             </button>
             <button
               className="lang-toggle"
               onClick={() => i18n.changeLanguage(i18n.language === 'bn' ? 'en' : 'bn')}
             >
               {i18n.language === 'bn' ? 'EN' : 'BN'}
             </button>
            <Link to="/" className="view-site-btn header-hide-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              View Site <ExternalLink size={14} />
            </Link>
            <div className="header-user-info header-hide-sm">
              <span>Welcome, {user?.first_name || user?.username || 'Admin'}</span>
            </div>
            <button onClick={handleLogout} className="header-logout-btn" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className="header-hide-sm">Logout</span>
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;